import { Component, EventEmitter, Input, Output } from "@angular/core";
import {
  animate,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-home-categories",
  templateUrl: "./home-categories.component.html",
  styleUrls: ["./home-categories.component.scss"],
  animations: [
    trigger("domainItemAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(8px) scale(0.98)" }),
        animate("180ms ease-out", style({ opacity: 1, transform: "none" })),
      ]),
      transition(":leave", [
        animate(
          "120ms ease-in",
          style({ opacity: 0, transform: "translateY(-6px) scale(0.98)" })
        ),
      ]),
    ]),
  ],
})
export class HomeCategoriesComponent {
  @Input() domains: any[] = [];
  @Input() showToggle = true;
  @Input() showTrustBadges = true;
  @Input() forceExpanded = false;
  @Output() domainSelected = new EventEmitter<any>();
  isExpanded = false;
  readonly collapsedItemsCount = 6;
  readonly trustBadges = [
    { icon: "shield-checkmark", textKey: "UX_TEXT.HOME_CATEGORIES.BADGES.VERIFIED_PROS", color: "#10b981" },
    { icon: "timer", textKey: "UX_TEXT.HOME_CATEGORIES.BADGES.RESPONSE_15", color: "#1d5d9b" },
    { icon: "logo-euro", textKey: "UX_TEXT.HOME_CATEGORIES.BADGES.FREE_QUOTE", color: "#eab308" },
    { icon: "card", textKey: "UX_TEXT.HOME_CATEGORIES.BADGES.SECURE_PAYMENT", color: "#60a5fa" },
  ];

  trackByDomain(index: number, domain: any) {
    return domain?.id ?? `${domain?.nom ?? "domain"}-${index}`;
  }

  /** Full-bleed card art: prefer `image`, then `icon` and other API keys. */
  getDomainCardImage(domain: any): string {
    return this.resolveDomainAssetUrl(domain, [
      "image",
      "icon",
      "logo",
      "file",
      "thumbnail",
      "icon_url",
      "image_url",
    ]);
  }

  /** Small top-right badge: prefer `icon`, then `image` and fallbacks. */
  getDomainBadgeImage(domain: any): string {
    return this.resolveDomainAssetUrl(domain, [
      "icon",
      "image",
      "logo",
      "file",
      "thumbnail",
      "icon_url",
      "image_url",
    ]);
  }

  private resolveDomainAssetUrl(domain: any, keyOrder: string[]): string {
    if (!domain) return "";
    for (const key of keyOrder) {
      const value = (domain[key] ?? "").toString().trim();
      if (!value) continue;
      return this.normalizeAssetUrl(value);
    }
    return "";
  }

  private normalizeAssetUrl(value: string): string {
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("//")) return `https:${value}`;

    const base = (environment.imagePath || "").replace(/\/$/, "");
    const normalized = value.startsWith("/") ? value : `/${value}`;
    return `${base}${normalized}`;
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  get shouldShowToggle(): boolean {
    return this.showToggle && this.domains.length > this.collapsedItemsCount;
  }

  get displayedDomains(): any[] {
    if (this.forceExpanded || this.isExpanded || !this.shouldShowToggle) {
      return this.domains;
    }
    return this.domains.slice(0, this.collapsedItemsCount);
  }
}
